import { useMemo } from "react";
import { Line } from "react-chartjs-2";
import { useSelector } from "react-redux";
import { FormatIndianNumber } from "../utiles/FormatIndianNumber";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler } from 'chart.js';
import useLocale from "../../src/locales/index";
import { useTranslation } from "react-i18next";
import numberSuffixes from "../utiles/numberSuffixes";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const MortgageChart = () => {
  const { textData } = useLocale();
  const { t, i18n } = useTranslation();
  const timeLabel = useMemo(() => t("month").charAt(0).toUpperCase(), [i18n.language]);
  const langCode = i18n.language || "default";
  const suffixSet = numberSuffixes[langCode] || numberSuffixes["default"];

  const {
    mortgageAmount,
    intrestRate,
    loanTerm,
    newLoanTerm,
    newIntrestRate,
  } = useSelector((state) => state.mortgageCalculator);
  const { mortgageData } = useSelector((state) => state.calculator);

  const {
    remainingBalance,
    newLoanAmount,
    monthlyPayment,
    newMonthlyPayment,
    monthlySaving,
    refinanceCost,
    breakEvenMonths,
    monthsElapsed,
    mortgagePointsCost,
    cashAdjustment
  } = useSelector((state) => state.calculator.mortgageData);

  const chartData = useMemo(() => {
    if (!mortgageData.remainingBalance || !mortgageData.newLoanAmount || !mortgageData.monthlyPayment) {
      return {
        labels: [],
        datasets: [],
      };
    }
    
    const principal = Number(mortgageAmount);
    const originalRate = Number(intrestRate) / 100 / 12;
    const newRate = Number(newIntrestRate) / 100 / 12;
    const originalTerm = Number(loanTerm) * 12;
    const newTerm = Number(newLoanTerm) * 12;
    
    const monthsElapsed = mortgageData.monthsElapsed || 0;

    const labels = [];
    
    // Use the calculated monthly payments from Redux store
    const originalMonthlyPayment = mortgageData.monthlyPayment;
    const newMonthlyPayment = mortgageData.newMonthlyPayment;

    // Generate data points for visualization in months
    const totalMonths = Math.max(originalTerm, monthsElapsed + newTerm);
    
    // Create month intervals - show approximately 15-20 data points
    const monthStep = Math.max(1, Math.floor(totalMonths / 15));
    
    const currentLoanBalanceData = [];
    const currentInterestData = [];
    const remortgageLoanBalanceData = [];
    const remortgageInterestData = [];

    for (let month = 0; month <= totalMonths; month += monthStep) {
      let currentLoanBalance = 0;
      let currentInterest = 0;
      let remortgageLoanBalance = null;
      let remortgageInterest = null;

      labels.push(month);

      if (month <= monthsElapsed) {
        if (originalRate === 0) {
          currentLoanBalance = Math.max(0, principal - originalMonthlyPayment * month);
          currentInterest = 0;
        } else {
          currentLoanBalance = Math.max(0, 
            principal * Math.pow(1 + originalRate, month) - 
            originalMonthlyPayment * ((Math.pow(1 + originalRate, month) - 1) / originalRate)
          );
          currentInterest = (originalMonthlyPayment * month) - (principal - currentLoanBalance);
        }
      } else {
        if (originalRate === 0) {
          currentLoanBalance = Math.max(0, principal - originalMonthlyPayment * month);
          currentInterest = 0;
        } else {
          currentLoanBalance = Math.max(0, 
            principal * Math.pow(1 + originalRate, month) - 
            originalMonthlyPayment * ((Math.pow(1 + originalRate, month) - 1) / originalRate)
          );
          currentInterest = (originalMonthlyPayment * month) - (principal - currentLoanBalance);
        }
      }

      if (month > monthsElapsed) {
        const newLoanMonths = month - monthsElapsed;
        if (newLoanMonths <= newTerm) {
          if (newRate === 0) {
            remortgageLoanBalance = Math.max(0, mortgageData.newLoanAmount - newMonthlyPayment * newLoanMonths);
            remortgageInterest = 0;
          } else {
            remortgageLoanBalance = Math.max(0, 
              mortgageData.newLoanAmount * Math.pow(1 + newRate, newLoanMonths) - 
              newMonthlyPayment * ((Math.pow(1 + newRate, newLoanMonths) - 1) / newRate)
            );
            remortgageInterest = (newMonthlyPayment * newLoanMonths) - (mortgageData.newLoanAmount - remortgageLoanBalance);
          }
        } else {
          remortgageLoanBalance = 0;
        }
      }

      currentLoanBalanceData.push(Math.round(currentLoanBalance));
      currentInterestData.push(Math.round(Math.max(0, currentInterest)));
      remortgageLoanBalanceData.push(remortgageLoanBalance !== null ? Math.round(remortgageLoanBalance) : null);
      remortgageInterestData.push(remortgageInterest !== null ? Math.round(Math.max(0, remortgageInterest)) : null);
    }

    return {
      labels,
      datasets: [
        {
          label: textData.chartLabels.currentLoan,
          data: currentLoanBalanceData,
          backgroundColor: "rgba(87, 198, 244, 0.2)",
          borderColor: "#57C6F4",
          pointBackgroundColor: "#57C6F4",
          pointBorderColor: "#ffffff",
          pointHoverBackgroundColor: "#ffffff",
          pointHoverBorderColor: "#57C6F4",
          pointRadius: 3,
          pointHoverRadius: 6,
          tension: 0.3,
          fill: false,
          borderWidth: 1,
        },
        {
          label:textData.chartLabels.refinanceLoan,
          data: remortgageLoanBalanceData,
          backgroundColor: "rgba(173, 216, 230, 0.2)",
          borderColor: "#FF8566",
          pointBackgroundColor: "#FF8566",
          pointBorderColor: "#ffffff",
          pointHoverBackgroundColor: "#ffffff",
          pointHoverBorderColor: "#FF8566",
          pointRadius: 3,
          pointHoverRadius: 6,
          tension: 0.3,
          fill: false,
          borderWidth: 1,
        },
        {
          label: textData.chartLabels.currentIntrest,
          data: currentInterestData,
          backgroundColor: "rgba(255, 197, 102, 0.2)",
          borderColor: "#FFC566",
          pointBackgroundColor: "#FFC566",
          pointBorderColor: "#ffffff",
          pointHoverBackgroundColor: "#ffffff",
          pointHoverBorderColor: "#FFC566",
          pointRadius: 3,
          pointHoverRadius: 6,
          tension: 0.3,
          fill: false,
          borderWidth: 1,
        },
        {
          label: textData.chartLabels.refinanceIntrest,
          data: remortgageInterestData,
          backgroundColor: "rgba(255, 133, 102, 0.2)",
          borderColor: "#4682B4",
          pointBackgroundColor: "#4682B4",
          pointBorderColor: "#ffffff",
          pointHoverBackgroundColor: "#ffffff",
          pointHoverBorderColor: "#4682B4",
          pointRadius: 3,
          pointHoverRadius: 6,
          tension: 0.3,
          fill: false,
          borderWidth: 1,
        }
      ]
    };
  }, [mortgageData]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    
    plugins: {
      title: {
        display: true,
        text: textData.mortgageCalc.chartTitle,
        align: 'start',
        font: {
          size: 14,
          weight: 'normal'
        },
        padding: {
          top: 10,
          bottom: 20
        }
      },
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        titleColor: "#333",
        bodyColor: "#333",
        borderColor: "#ddd",
        borderWidth: 1,
        padding: 10,
        cornerRadius: 4,
        displayColors: true,
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += `${textData.currency} ` + FormatIndianNumber(Math.round(context.parsed.y));
            }
            return label;
          },
          title: function (context) {
            return `Month: ${context[0].label}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false,
          drawOnChartArea: false,
        },
        border: {
          dash: [4, 4],
          color: 'rgba(0, 0, 0, 0.3)',
          width: 1,
        },
        title: {
          display: true,
          text: 'Months' // Changed from years to months
        },
        ticks: {
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 7,
          callback: function(value, index, ticks) {
            // Show the actual month value
            return this.getLabelForValue(value);
          }
        }
      },
      y: {
        grid: {
          display: true,
          drawBorder: true,
          color: "rgba(200, 200, 200, 0.3)",
          lineWidth: 1,
          drawTicks: false,
          drawOnChartArea: true,
        },
        border: {
          dash: [4, 4],
          width: 0,
        },
        ticks: {
          callback: function (value) {
              if (value >= 10000000) return (value / 10000000).toFixed(0) + suffixSet.crore;
              if (value >= 100000) return (value / 100000).toFixed(0) + suffixSet.lakh;
              if (value >= 1000) return (value / 1000).toFixed(0) + suffixSet.thousand;
              return value.toLocaleString(i18n.language);
            },
          maxTicksLimit: 8
        },
        beginAtZero: true,
      }
    },
  };

  const formatCurrency = (value) => {
    if (value === undefined || value === null) return "0";
    return Math.round(value).toLocaleString(i18n.language || "en-IN");
  };

  return (
    <div className="flex flex-col w-full">
      {/* Chart Container */}
      <div className="bg-white pt-6 px-4 pb-4 mb-2">
        <div className="h-[325px]">
          <Line data={chartData} options={options} />
        </div>
      </div>

      <div className="flex justify-between pt-6 pb-5 sm:flex-row flex-col">
        <div className="">
          <div className="flex gap-2 items-center">
            <div className="w-5 h-5 bg-[#57C6F4] rounded-[4px]"></div>
            <span className="text-sm text-start">
            {textData.chartLabels.currentLoan}
            </span>
          </div>
          <div className="flex gap-2 items-center pt-3 sm:pb-0 pb-3">
            <div className="w-5 h-5 bg-[#FFC566] rounded-[4px]"></div>
            <span className="text-sm text-start">

            {textData.chartLabels.currentIntrest}
            </span>
          </div>
        </div>
        <div className="">
          <div className="flex gap-2 items-center">
            <div className="w-5 h-5 bg-[#FF8566] rounded-[4px]"></div>
            <span className="text-sm text-start">
            {textData.chartLabels.refinanceLoan}

            </span>
          </div>
          <div className="flex gap-2 items-center pt-3">
            <div className="w-5 h-5 bg-[#4682B4] rounded-[4px]"></div>
            <span className="text-sm text-start">
            {textData.chartLabels.refinanceIntrest}

            </span>
          </div>
        </div>
      </div>

      <div className="border-b border-solid border-gray-light"></div>

      <div className="pt-6">
        <div className="flex items-center py-[10px]">
          <div className="flex-grow text-left text-sm sm:text-sm text-black flex justify-between">
            {textData.chartLabels.remainingBalance}

            <span className="font-medium">{textData.currency} {formatCurrency(remainingBalance)}</span>
          </div>
        </div>

        <div className="flex items-center py-[10px]">
          <div className="flex-grow text-left text-sm sm:text-sm text-black flex justify-between">
            {textData.chartLabels.newLoanpay}

            <span className="font-medium">{textData.currency} {formatCurrency(newLoanAmount)}</span>
          </div>
        </div>

        <div className="flex items-center py-[10px]">
          <div className="flex-grow text-left text-sm sm:text-sm text-black flex justify-between">
            {textData.chartLabels.monthlyPay}

            <span className="font-medium">{textData.currency} {formatCurrency(monthlyPayment)}</span>
          </div>
        </div>

        <div className="flex items-center py-[10px]">
          <div className="flex-grow text-left text-sm sm:text-sm text-black flex justify-between">
            {textData.chartLabels.newMonthlyPay}

            <span className="font-medium">{textData.currency} {formatCurrency(newMonthlyPayment)}</span>
          </div>
        </div>

        <div className="flex items-center py-[10px]">
          <div className="flex-grow text-left text-sm sm:text-sm text-black flex justify-between">
            {textData.chartLabels.monthlySaving}

            <span className="font-medium">{textData.currency} {formatCurrency(monthlySaving)}</span>
          </div>
        </div>

        <div className="flex items-center py-[10px]">
          <div className="flex-grow text-left text-sm sm:text-sm text-black flex justify-between">
            {textData.chartLabels.refinanceCost}

            <span className="font-medium">{textData.currency} {formatCurrency(refinanceCost)}</span>
          </div>
        </div>

        <div className="flex items-center py-[10px] mb-3">
          <div className="flex-grow text-left text-sm sm:text-sm text-black flex justify-between">
            {textData.chartLabels.breackEven}

            <span className="font-medium">{timeLabel} {breakEvenMonths ? formatCurrency(breakEvenMonths) : "0"}</span>
          </div>
        </div>

        <div className="border-b border-solid border-gray-light"></div>

        <div className="flex items-center py-[10px] mt-3">
          <div className="flex-grow text-left text-sm sm:text-sm text-black flex justify-between">
            {textData.chartLabels.elapsed}

            <span className="font-medium">{timeLabel} {monthsElapsed ? formatCurrency(monthsElapsed) : "0"}</span>
          </div>
        </div>

        <div className="flex items-center py-[10px]">
          <div className="flex-grow text-left text-sm sm:text-sm text-black flex justify-between">
            {textData.chartLabels.mortgagePoint}

            <span className="font-medium">{textData.currency} {formatCurrency(mortgagePointsCost)}</span>
          </div>
        </div>

        <div className="flex items-center py-[10px]">
          <div className="flex-grow text-left text-sm sm:text-sm text-black flex justify-between">
            {textData.chartLabels.cashInOut}

            <span className="font-medium">{textData.currency} {formatCurrency(cashAdjustment)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MortgageChart;